import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { matchesQuery } from "../../boundaries/text-search.search";
import {
  ISSUE_LIFECYCLE_FILTERS,
  ISSUE_SEVERITY_FILTERS,
  ISSUE_SORT_KEY,
  ISSUE_SORT_KEYS,
  ISSUE_STATUS_FILTER,
  ISSUE_STATUS_FILTERS,
  type IssueLifecycleFilter,
  type IssueSeverityFilter,
  type IssueSortKey,
  type IssueStatusFilter,
} from "../../enums/issues";
import { rankIssuesForClientPriority } from "../scan/issuePresentationPriority";
import { isOpen, pillarOf, SEVERITY_ORDER } from "../website/issueModel";

export type Filters = {
  status: IssueStatusFilter;
  lifecycle: IssueLifecycleFilter;
  severity: IssueSeverityFilter;
  pillar: string;
  query: string;
  sort: IssueSortKey;
};

export function readFilters(params: URLSearchParams): Filters {
  return {
    status:
      enumValue(params.get("status"), ISSUE_STATUS_FILTERS) ??
      ISSUE_STATUS_FILTER.UNRESOLVED,
    lifecycle: enumValue(params.get("lc"), ISSUE_LIFECYCLE_FILTERS) ?? "all",
    severity: enumValue(params.get("sev"), ISSUE_SEVERITY_FILTERS) ?? "all",
    pillar: params.get("pillar") ?? "all",
    query: params.get("q") ?? "",
    sort:
      enumValue(params.get("sort"), ISSUE_SORT_KEYS) ?? ISSUE_SORT_KEY.IMPACT,
  };
}

export function applyFilters(
  data: OverviewResponse,
  filters: Filters,
): Issue[] {
  const query = filters.query.trim().toLowerCase();
  const filtered = data.issues.filter((issue) => {
    if (!matchStatus(issue, filters.status)) {
      return false;
    }
    if (
      filters.lifecycle !== "all" &&
      issue.lifecycleState !== filters.lifecycle
    ) {
      return false;
    }
    if (filters.severity !== "all" && issue.severity !== filters.severity) {
      return false;
    }
    if (
      filters.pillar !== "all" &&
      pillarOf(issue.metricKey) !== filters.pillar
    ) {
      return false;
    }
    if (query) {
      const paths = issue.pages.map((page) => page.path).join(" ");
      if (!matchesQuery(query, issue.title, paths)) {
        return false;
      }
    }
    return true;
  });
  return sortIssues(data, filtered, filters.sort);
}

function matchStatus(issue: Issue, status: IssueStatusFilter): boolean {
  if (status === ISSUE_STATUS_FILTER.ALL) {
    return true;
  }
  if (status === ISSUE_STATUS_FILTER.UNRESOLVED) {
    return isOpen(issue);
  }
  return issue.status === status;
}

function sortIssues(
  data: OverviewResponse,
  issues: Issue[],
  sort: IssueSortKey,
): Issue[] {
  const copy = [...issues];
  if (sort === ISSUE_SORT_KEY.IMPACT) {
    return rankIssuesForClientPriority(data, copy);
  }
  if (sort === ISSUE_SORT_KEY.SEVERITY) {
    return copy.sort(
      (left, right) =>
        SEVERITY_ORDER[left.severity] - SEVERITY_ORDER[right.severity] ||
        right.impact - left.impact,
    );
  }
  return copy.sort(
    (left, right) =>
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
  );
}

function enumValue<T extends string>(
  value: string | null,
  values: readonly T[],
): T | undefined {
  return value !== null && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}
